const MAX_TRACKS = 16;
const MAX_VIEWPORTS = 16;

const cell = (slot, row, column, rowSpan, columnSpan) => ({ slot, row, column, rowSpan, columnSpan });

export const MIXED_VIEWPORT_LAYOUT_PRESETS = Object.freeze({
  '2-over-3': Object.freeze({
    presetId: '2-over-3',
    label: '2 over 3 (5 viewports)',
    rows: 2,
    cols: 3,
    geometry: Object.freeze({
      version: 1,
      tracks: Object.freeze({ rows: 2, columns: 6 }),
      cells: Object.freeze([
        cell(0, 0, 0, 1, 3), cell(1, 0, 3, 1, 3),
        cell(2, 1, 0, 1, 2), cell(3, 1, 2, 1, 2), cell(4, 1, 4, 1, 2),
      ]),
    }),
  }),
  '2x2-plus-1x3': Object.freeze({
    presetId: '2x2-plus-1x3',
    label: '2×2 + 1×3 (7 viewports)',
    rows: 2,
    cols: 4,
    geometry: Object.freeze({
      version: 1,
      tracks: Object.freeze({ rows: 6, columns: 3 }),
      cells: Object.freeze([
        cell(0, 0, 0, 3, 1), cell(1, 0, 1, 3, 1),
        cell(2, 3, 0, 3, 1), cell(3, 3, 1, 3, 1),
        cell(4, 0, 2, 2, 1), cell(5, 2, 2, 2, 1), cell(6, 4, 2, 2, 1),
      ]),
    }),
  }),
});

const positiveInt = (value, fallback = 1) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

export const validateViewportGeometry = (geometry) => {
  const issues = [];
  if (!geometry || typeof geometry !== 'object' || Array.isArray(geometry)) {
    return { valid: false, issues: ['geometry must be an object'] };
  }
  if (geometry.version !== 1) issues.push('geometry version must be 1');
  const rows = Number(geometry.tracks?.rows);
  const columns = Number(geometry.tracks?.columns);
  if (!Number.isInteger(rows) || rows < 1 || rows > MAX_TRACKS) issues.push(`row tracks must be 1-${MAX_TRACKS}`);
  if (!Number.isInteger(columns) || columns < 1 || columns > MAX_TRACKS) issues.push(`column tracks must be 1-${MAX_TRACKS}`);
  const cells = geometry.cells;
  if (!Array.isArray(cells) || cells.length < 1 || cells.length > MAX_VIEWPORTS) {
    issues.push(`cells must contain 1-${MAX_VIEWPORTS} viewports`);
    return { valid: false, issues };
  }
  if (issues.length) return { valid: false, issues };

  const occupied = Array.from({ length: rows }, () => Array(columns).fill(null));
  const slots = new Set();
  cells.forEach((candidate, index) => {
    const slot = Number(candidate?.slot);
    const row = Number(candidate?.row);
    const column = Number(candidate?.column);
    const rowSpan = Number(candidate?.rowSpan);
    const columnSpan = Number(candidate?.columnSpan);
    if (!Number.isInteger(slot) || slot < 0 || slot >= cells.length) issues.push(`cell ${index} has an invalid slot`);
    if (slots.has(slot)) issues.push(`cell ${index} duplicates slot ${slot}`);
    slots.add(slot);
    if (![row, column].every(Number.isInteger) || row < 0 || column < 0 ||
      ![rowSpan, columnSpan].every(Number.isInteger) || rowSpan < 1 || columnSpan < 1 ||
      row + rowSpan > rows || column + columnSpan > columns) {
      issues.push(`cell ${index} is out of bounds`);
      return;
    }
    for (let r = row; r < row + rowSpan; r += 1) {
      for (let c = column; c < column + columnSpan; c += 1) {
        if (occupied[r][c] !== null) issues.push(`cell ${index} overlaps slot ${occupied[r][c]}`);
        occupied[r][c] = slot;
      }
    }
  });
  if (slots.size !== cells.length || [...Array(cells.length).keys()].some(slot => !slots.has(slot))) {
    issues.push('slots must be unique and contiguous from zero');
  }
  if (occupied.some(row => row.some(slot => slot === null))) issues.push('geometry must not leave unused holes');
  return { valid: issues.length === 0, issues: [...new Set(issues)] };
};

const normalizeGeometry = (geometry) => {
  const validation = validateViewportGeometry(geometry);
  if (!validation.valid) return undefined;
  return {
    version: 1,
    tracks: { rows: Number(geometry.tracks.rows), columns: Number(geometry.tracks.columns) },
    cells: geometry.cells
      .map(candidate => ({
        slot: Number(candidate.slot), row: Number(candidate.row), column: Number(candidate.column),
        rowSpan: Number(candidate.rowSpan), columnSpan: Number(candidate.columnSpan),
      }))
      .sort((a, b) => a.slot - b.slot),
  };
};

export const normalizeViewportLayout = (layout = {}) => {
  const source = layout && typeof layout === 'object' && !Array.isArray(layout) ? layout : {};
  const rows = Math.min(MAX_TRACKS, positiveInt(source.rows, 2));
  const cols = Math.min(MAX_TRACKS, positiveInt(source.cols, 2));
  const geometry = normalizeGeometry(source.geometry);
  const normalized = { ...source, rows, cols, label: source.label || `${rows}×${cols}` };
  if (geometry) normalized.geometry = geometry;
  else delete normalized.geometry;
  if (!geometry) delete normalized.presetId;
  return normalized;
};

export const getViewportCount = (layout = {}) => {
  const normalized = normalizeViewportLayout(layout);
  return normalized.geometry?.cells.length || normalized.rows * normalized.cols;
};

export const getViewportGridStyle = (layout = {}) => {
  const normalized = normalizeViewportLayout(layout);
  if (!normalized.geometry) {
    return {
      gridTemplateColumns: `repeat(${normalized.cols}, minmax(0, 1fr))`,
      gridTemplateRows: `repeat(${normalized.rows}, minmax(0, 1fr))`,
    };
  }
  return {
    gridTemplateColumns: `repeat(${normalized.geometry.tracks.columns}, minmax(0, 1fr))`,
    gridTemplateRows: `repeat(${normalized.geometry.tracks.rows}, minmax(0, 1fr))`,
  };
};

export const getViewportCellStyle = (layout = {}, slot = 0) => {
  const normalized = normalizeViewportLayout(layout);
  const candidate = normalized.geometry?.cells.find(item => item.slot === slot);
  if (!candidate) return undefined;
  return {
    gridRow: `${candidate.row + 1} / span ${candidate.rowSpan}`,
    gridColumn: `${candidate.column + 1} / span ${candidate.columnSpan}`,
  };
};
